import {
  BASE_API_URL,
  CREATE_UPDATE_BILLING_STATEMENT_DETAILS_URL,
  CREATE_UPDATE_BILLING_STATEMENT_URL,
  GET_ALL_BILLING_FILTER_URL,
  GET_ALL_PROJECT_FILTER_URL,
  GET_BILLING_STATEMENT_DETAILS_BY_ID_URL,
  GET_BROKER_BILLING_URL,
  GET_PROJECT_DETAILS_BY_ID_URL,
} from "../../constants/ApiRoute";
import API from "../comman/API";
import { authHeader, getLogUserId } from "../comman/MainFunction";
import { deCryptFun, enCryptFun } from "../comman/CustomFunction";

const runRequest = async (config) => {
  const response = await API(config);
  return JSON.parse(deCryptFun(response.data));
};

export const parseBillingApiError = (error) => {
  if (!error?.response?.data) {
    return { message: "Something went wrong. Please try again." };
  }

  try {
    return JSON.parse(deCryptFun(error.response.data));
  } catch (parseError) {
    return { message: "Something went wrong. Please try again." };
  }
};

export const fetchAssignedBillingProjects = async () => {
  const config = {
    method: "GET",
    url: GET_BROKER_BILLING_URL,
    headers: authHeader(),
    params: {
      webString: enCryptFun(
        JSON.stringify({
          customerId: "",
          projectId: "",
          brokerId: getLogUserId(),
        })
      ),
    },
  };

  const parsedResponse = await runRequest(config);
  const data = parsedResponse?.data || [];

  return {
    customers: data.map((customer) => ({
      CustomerID: customer.CustomerID,
      CustomerName: customer.CustomerName,
    })),
    assignedProjects: data.flatMap((customer) =>
      (customer.projectDetails || []).map((project) => ({
        CustomerID: customer.CustomerID,
        ProjectID: project.ProjectID,
        ProjectName: project.ProjectName,
      }))
    ),
  };
};

export const fetchProjectsForCustomer = async (customerId, assignedProjects = []) => {
  if (!customerId) {
    return [];
  }

  const config = {
    method: "GET",
    url: GET_ALL_PROJECT_FILTER_URL,
    headers: authHeader(),
    params: {
      webString: enCryptFun(
        JSON.stringify({
          count: 100,
          offset: 0,
          customerId,
        })
      ),
    },
  };

  const parsedResponse = await runRequest(config);
  const allowedProjectIds = new Set(
    assignedProjects
      .filter((project) => String(project.CustomerID) === String(customerId))
      .map((project) => String(project.ProjectID))
  );

  return (parsedResponse?.data || [])
    .filter((project) => allowedProjectIds.has(String(project.ProjectId ?? project.ProjectID ?? "")))
    .map((project) => ({
      ProjectId: project.ProjectId ?? project.ProjectID ?? "",
      Name: project.Name ?? project.ProjectName ?? "",
      BillingPeriod: project.BillingPeriod,
      BillingDayRate:
        project.BillingDayRate ?? project.billingDayRate ?? project.DayRate ?? 0,
      BillableOfficePerDiem:
        project.BillableOfficePerDiem ??
        project.billableOfficePerDiem ??
        project.PayableOfficePerDiem ??
        project.payableOfficePerDiem ??
        project.OfficePerDiem ??
        project.officePerDiem ??
        0,
      BillableTravelPerDiem:
        project.BillableTravelPerDiem ??
        project.billableTravelPerDiem ??
        project.PayableTravelPerDiem ??
        project.payableTravelPerDiem ??
        project.TravelPerDiem ??
        project.travelPerDiem ??
        0,
      OfficePerDiem:
        project.OfficePerDiem ?? project.officePerDiem ?? project.OfficePerDiemRate ?? 0,
      TravelPerDiem:
        project.TravelPerDiem ?? project.travelPerDiem ?? project.TravelPerDiemRate ?? 0,
      OfficePerDiemRate:
        project.OfficePerDiemRate ??
        project.officePerDiemRate ??
        project.BillableOfficePerDiem ??
        project.billableOfficePerDiem ??
        0,
      TravelPerDiemRate:
        project.TravelPerDiemRate ??
        project.travelPerDiemRate ??
        project.BillableTravelPerDiem ??
        project.billableTravelPerDiem ??
        0,
      PayableOfficePerDiem:
        project.PayableOfficePerDiem ?? project.payableOfficePerDiem ?? 0,
      PayableTravelPerDiem:
        project.PayableTravelPerDiem ?? project.payableTravelPerDiem ?? 0,
      Active: project.Active,
    }));
};

export const getProjectById = async (projectId) => {
  if (!projectId) return null;

  const config = {
    method: "GET",
    url: GET_PROJECT_DETAILS_BY_ID_URL,
    headers: authHeader(),
    params: {
      webString: enCryptFun(
        JSON.stringify({
          projectId,
        }),
      ),
    },
  };

  return runRequest(config);
};

export const findBillingStatement = async ({ projectId, billingStart, billingEnd }) => {
  const config = {
    method: "GET",
    url: GET_ALL_BILLING_FILTER_URL,
    headers: authHeader(),
    params: {
      webString: enCryptFun(
        JSON.stringify({
          projectId,
          billingStart,
          billingEnd,
          brokerId: getLogUserId(),
        })
      ),
    },
  };

  const parsedResponse = await runRequest(config);
  const data = parsedResponse?.data || [];

  return (
    data.find(
      (item) =>
        String(item.BillingStart || "").includes(billingStart) &&
        String(item.BillingEnd || "").includes(billingEnd)
    ) || null
  );
};

export const createBillingStatement = async (statementData) => {
  const config = {
    method: "POST",
    url: CREATE_UPDATE_BILLING_STATEMENT_URL,
    headers: authHeader(),
    data: {
      webString: enCryptFun(JSON.stringify(statementData)),
    },
  };

  return runRequest(config);
};

export const fetchBillingStatementDetails = async (headerId) => {
  const config = {
    method: "GET",
    url: GET_BILLING_STATEMENT_DETAILS_BY_ID_URL,
    headers: authHeader(),
    params: {
      webString: enCryptFun(
        JSON.stringify({
          headerId: parseInt(headerId, 10),
        })
      ),
    },
  };

  const parsedResponse = await runRequest(config);
  return parsedResponse?.billingStatementDetails || [];
};

export const upsertBillingStatementDetail = async (detailData) => {
  const config = {
    method: "POST",
    url: CREATE_UPDATE_BILLING_STATEMENT_DETAILS_URL,
    headers: authHeader(),
    data: {
      webString: enCryptFun(JSON.stringify(detailData)),
    },
  };

  return runRequest(config);
};

export const fetchDailyEntryBilling = async ({ startDate, endDate }) => {
  const config = {
    method: "GET",
    url: `${BASE_API_URL}billStatement/getDailyEntryBilling`,
    headers: authHeader(),
    params: {
      webString: enCryptFun(
        JSON.stringify({
          brokerId: getLogUserId(),
          startDate,
          endDate,
        })
      ),
    },
  };

  const parsedResponse = await runRequest(config);
  return parsedResponse?.data || [];
};

export const fetchDailyEntryBillingByDay = async ({ date }) => {
  const config = {
    method: "GET",
    url: `${BASE_API_URL}billStatement/getDailyEntryBillingByDate`,
    headers: authHeader(),
    params: {
      webString: enCryptFun(
        JSON.stringify({
          brokerId: getLogUserId(),
          date,
        })
      ),
    },
  };

  return runRequest(config);
};

export const checkStatementExistsForDate = async ({ projectId, date }) => {
  const config = {
    method: "GET",
    url: `${BASE_API_URL}billStatement/checkStatementExistsForDate`,
    headers: authHeader(),
    params: {
      webString: enCryptFun(
        JSON.stringify({
          brokerId: getLogUserId(),
          projectId,
          date,
        }),
      ),
    },
  };

  return runRequest(config);
};

export const upsertDailyEntryStatement = async (payload) => {
  const config = {
    method: "POST",
    url: `${BASE_API_URL}billStatement/upsertDailyEntryStatement`,
    headers: authHeader(),
    data: {
      webString: enCryptFun(JSON.stringify(payload)),
    },
  };

  return runRequest(config);
};
